// src/views/ExpensesView.js
import React, { useState, useEffect, useMemo } from 'react';
import { db, functions } from '../firebase/config';
import { collection, query, onSnapshot, orderBy, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { HIGH_LEVEL_ROLES } from '../utils/permissions'; // x5rules-8d3f2a61
import { httpsCallable } from 'firebase/functions';
import { 
    Plus, Edit, Trash2, Calendar, RefreshCw, Loader2, Link as LinkIcon, 
    BarChart3, ArrowUpDown, ArrowUp, ArrowDown, XCircle 
} from 'lucide-react'; 
import ExpenseFormModal from '../components/expenses/ExpenseFormModal';
import ConfirmationModal from '../components/common/ConfirmationModal';
import { format, startOfMonth, endOfMonth, startOfYear, endOfYear, isWithinInterval } from 'date-fns';
import { formatCurrency } from '../utils/formatters'; 

// --- INTERACTIVE KPI CARD COMPONENT ---
const ExpenseKpiCard = ({ title, amount, subtext, icon, colorClass, onClick, isActive }) => (
    <div 
        onClick={onClick}
        className={`bg-white p-5 rounded-lg border shadow-sm flex items-center justify-between cursor-pointer transition-all duration-200 
        ${isActive ? 'ring-2 ring-blue-500 border-transparent bg-blue-50' : 'hover:border-blue-300 hover:shadow-md'}`}
    >
        <div>
            <p className={`text-xs font-bold uppercase tracking-wider mb-1 ${isActive ? 'text-blue-700' : 'text-gray-500'}`}>{title}</p>
            <h3 className={`text-2xl font-bold ${colorClass}`}>{formatCurrency(amount)}</h3>
            {subtext && <p className={`text-xs mt-1 ${isActive ? 'text-blue-600' : 'text-gray-400'}`}>{subtext}</p>}
        </div>
        <div className={`p-3 rounded-full ${isActive ? 'bg-white' : colorClass.replace('text-', 'bg-').replace('600', '100')}`}>
            {icon}
        </div>
    </div>
);

// x5rules-8d3f2a61 — X5 (#21): Owner/Admin/Manager may edit and delete any expense; everyone else
// may edit only their OWN (createdBy, or userId on an older phone record) — the same test
// firestore.rules makes. Delete is high-level only there, so the bin shows only to them.
const canEditExpense = (profile, expense) => !!profile && (HIGH_LEVEL_ROLES.includes(profile.role)
    || expense?.createdBy === profile.id || expense?.userId === profile.id);
const canDeleteExpense = (profile) => !!profile && HIGH_LEVEL_ROLES.includes(profile.role);

const ExpensesView = ({ currentUserProfile }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingExpense, setEditingExpense] = useState(null);
    const [expenseToDelete, setExpenseToDelete] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [syncingId, setSyncingId] = useState(null); 
    
    // Sorting & Filtering State
    const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
    const [activeFilter, setActiveFilter] = useState(null); 

    const [expenses, setExpenses] = useState([]);
    const [jobs, setJobs] = useState([]); 
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (!currentUserProfile?.organizationId) {
            setIsLoading(false);
            return;
        }
        
        const orgId = currentUserProfile.organizationId;
        
        // Fetch expenses
        const expensesRef = collection(db, `organizations/${orgId}/expenses`);
        const qExpenses = query(expensesRef, orderBy('date', 'desc'));
        const unsubExpenses = onSnapshot(qExpenses, (snapshot) => {
            setExpenses(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setIsLoading(false);
        });

        // Fetch jobs for linking
        const jobsRef = collection(db, `organizations/${orgId}/jobs`);
        const qJobs = query(jobsRef, orderBy('createdAt', 'desc'));
        const unsubJobs = onSnapshot(qJobs, (snapshot) => {
            setJobs(snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }))); // s6costing-docid: doc id wins over a stale stored `id` field
        });

        return () => {
            unsubExpenses();
            unsubJobs();
        };
    }, [currentUserProfile?.organizationId]);

    // --- METRICS CALCULATION ---
    const metrics = useMemo(() => {
        const now = new Date();
        const thisMonthInterval = { start: startOfMonth(now), end: endOfMonth(now) };
        const thisYearInterval = { start: startOfYear(now), end: endOfYear(now) };

        let monthTotal = 0;
        let yearTotal = 0;
        let unlinkedTotal = 0;

        expenses.forEach(exp => {
            const amount = parseFloat(exp.amount) || 0;
            const date = exp.date?.toDate ? exp.date.toDate() : new Date();

            if (isWithinInterval(date, thisMonthInterval)) monthTotal += amount;
            if (isWithinInterval(date, thisYearInterval)) yearTotal += amount;
            if (!exp.jobId) unlinkedTotal += amount;
        });

        return { monthTotal, yearTotal, unlinkedTotal };
    }, [expenses]);

    // --- UPDATED: RICH JOB INFO HELPER ---
    const getJobDisplayInfo = (jobId) => {
        if (!jobId) return '';
        const job = jobs.find(j => j.id === jobId);
        if (!job) return 'Unknown Job';

        const parts = [];
        if (job.jobNumber) parts.push(`#${job.jobNumber}`);
        parts.push(job.title || 'Untitled');
        if (job.clientName) parts.push(job.clientName);
        else if (job.customerDetails?.name) parts.push(job.customerDetails.name);

        return parts.join(' - ');
    };

    const filteredAndSortedExpenses = useMemo(() => {
        let processed = [...expenses];

        // 1. Apply Filters
        if (activeFilter === 'month') {
            const now = new Date();
            const range = { start: startOfMonth(now), end: endOfMonth(now) };
            processed = processed.filter(e => {
                const d = e.date?.toDate ? e.date.toDate() : new Date();
                return isWithinInterval(d, range);
            });
        } else if (activeFilter === 'year') {
            const now = new Date();
            const range = { start: startOfYear(now), end: endOfYear(now) };
            processed = processed.filter(e => {
                const d = e.date?.toDate ? e.date.toDate() : new Date();
                return isWithinInterval(d, range);
            });
        } else if (activeFilter === 'unlinked') {
            processed = processed.filter(e => !e.jobId);
        }

        // 2. Apply Sorting
        if (sortConfig.key) {
            processed.sort((a, b) => {
                let aVal = a[sortConfig.key];
                let bVal = b[sortConfig.key];

                if (sortConfig.key === 'jobId') {
                    // Sort using the rich display string
                    aVal = getJobDisplayInfo(a.jobId) || 'zz'; 
                    bVal = getJobDisplayInfo(b.jobId) || 'zz';
                }
                else if (sortConfig.key === 'date') {
                    aVal = a.date?.toDate ? a.date.toDate().getTime() : 0;
                    bVal = b.date?.toDate ? b.date.toDate().getTime() : 0;
                }
                else if (sortConfig.key === 'vendor') {
                    aVal = (a.vendor || '').toLowerCase();
                    bVal = (b.vendor || '').toLowerCase();
                }
                else if (sortConfig.key === 'amount') {
                    aVal = parseFloat(a.amount || 0);
                    bVal = parseFloat(b.amount || 0);
                }

                if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return processed;
    }, [expenses, sortConfig, activeFilter, jobs]);

    const handleSort = (key) => {
        setSortConfig(current => ({
            key,
            direction: current.key === key && current.direction === 'desc' ? 'asc' : 'desc'
        }));
    };

    const handleFilterToggle = (filterType) => {
        if (activeFilter === filterType) {
            setActiveFilter(null); 
        } else {
            setActiveFilter(filterType);
        }
    };

    const SortIcon = ({ columnKey }) => {
        if (sortConfig.key !== columnKey) return <ArrowUpDown size={14} className="ml-1 text-gray-400 inline" />;
        return sortConfig.direction === 'asc' 
            ? <ArrowUp size={14} className="ml-1 text-blue-600 inline" /> 
            : <ArrowDown size={14} className="ml-1 text-blue-600 inline" />;
    };

    // --- CRUD & SYNC HANDLERS ---
    const openExpenseModal = (expense = null) => {
        setEditingExpense(expense);
        setIsModalOpen(true);
    };
    const closeExpenseModal = () => {
        setEditingExpense(null);
        setIsModalOpen(false);
    };
    
    const handleSaveExpense = async (expenseData, expenseId) => {
        if (!currentUserProfile?.organizationId) throw new Error("Organization not found.");
        const orgId = currentUserProfile.organizationId;
        
        if (expenseId) {
            const expenseRef = doc(db, `organizations/${orgId}/expenses`, expenseId);
            await updateDoc(expenseRef, { ...expenseData, updatedAt: serverTimestamp() });
        } else {
            const expensesRef = collection(db, `organizations/${orgId}/expenses`);
            await addDoc(expensesRef, { ...expenseData, createdAt: serverTimestamp(), createdBy: currentUserProfile.id });
        }
    };

    const handleDeleteRequest = (expense) => {
        setExpenseToDelete(expense);
    };

    const handleConfirmDelete = async () => {
        if (!expenseToDelete) return;
        setIsProcessing(true);
        try {
            const orgId = currentUserProfile.organizationId;
            const expenseRef = doc(db, `organizations/${orgId}/expenses`, expenseToDelete.id);
            await deleteDoc(expenseRef);
            setExpenseToDelete(null);
        } catch (error) {
            console.error("Error deleting expense:", error);
            alert("Failed to delete expense.");
        } finally {
            setIsProcessing(false);
        }
    };

    const handleSyncToQB = async (expense) => {
        if (!window.confirm(`Sync "${expense.vendor} - $${expense.amount}" to QuickBooks?`)) return;
        
        setSyncingId(expense.id);
        try {
            const syncFn = httpsCallable(functions, 'syncExpenseToQuickBooks');
            const result = await syncFn({ expenseId: expense.id });
            
            if (result.data.success) {
                alert("Successfully synced to QuickBooks!");
            }
        } catch (error) {
            console.error("QB Sync Error:", error);
            alert(`Sync Failed: ${error.message}`);
        } finally {
            setSyncingId(null);
        }
    };
    
    return (
        <>
            <div className="p-6 md:p-10 bg-gray-50 min-h-screen">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-800">Expenses</h1>
                        <p className="text-gray-500 mt-1">Track costs and sync with accounting</p>
                    </div>
                    <button onClick={() => openExpenseModal()} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md font-semibold hover:bg-blue-700 shadow-sm transition-colors">
                        <Plus size={16} />
                        <span>Add Expense</span>
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <ExpenseKpiCard 
                        title="Expenses (This Month)" 
                        amount={metrics.monthTotal} 
                        icon={<Calendar size={24} />} 
                        colorClass="text-blue-600" 
                        onClick={() => handleFilterToggle('month')}
                        isActive={activeFilter === 'month'}
                    />
                    <ExpenseKpiCard 
                        title="Expenses (YTD)" 
                        amount={metrics.yearTotal} 
                        icon={<BarChart3 size={24} />} 
                        colorClass="text-indigo-600" 
                        onClick={() => handleFilterToggle('year')}
                        isActive={activeFilter === 'year'}
                    />
                    <ExpenseKpiCard 
                        title="Unassigned Costs" 
                        amount={metrics.unlinkedTotal} 
                        subtext="Expenses not linked to a job"
                        icon={<LinkIcon size={24} />} 
                        colorClass="text-orange-600" 
                        onClick={() => handleFilterToggle('unlinked')}
                        isActive={activeFilter === 'unlinked'}
                    />
                </div>
                
                {activeFilter && (
                    <div className="mb-4 flex items-center justify-between bg-blue-50 border border-blue-100 px-4 py-3 rounded-md animate-fade-in">
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-blue-800">Active Filter:</span>
                            <span className="text-sm text-blue-600">
                                {activeFilter === 'month' && "Current Month Only"}
                                {activeFilter === 'year' && "Current Year Only"}
                                {activeFilter === 'unlinked' && "Unassigned Expenses Only"}
                            </span>
                        </div>
                        <button 
                            onClick={() => setActiveFilter(null)}
                            className="text-blue-500 hover:text-blue-700 flex items-center gap-1 text-sm font-medium transition-colors"
                        >
                            <XCircle size={16} />
                            Clear Filter
                        </button>
                    </div>
                )}

                <div className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200">
                    {/* --- DESKTOP TABLE --- */}
                    <div className="hidden md:block overflow-x-auto max-h-[70vh]">
                        <table className="w-full text-sm text-left relative">
                            <thead className="text-xs text-gray-700 uppercase bg-gray-50 border-b sticky top-0 z-10 shadow-sm">
                                <tr>
                                    <th className="px-6 py-4 cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('date')}>
                                        Date <SortIcon columnKey="date" />
                                    </th>
                                    <th className="px-6 py-4 cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('vendor')}>
                                        Vendor & Category <SortIcon columnKey="vendor" />
                                    </th>
                                    <th className="px-6 py-4 cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('jobId')}>
                                        Linked Job <SortIcon columnKey="jobId" />
                                    </th>
                                    <th className="px-6 py-4 text-right cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('amount')}>
                                        Amount <SortIcon columnKey="amount" />
                                    </th>
                                    <th className="px-6 py-4 text-center">Sync</th>
                                    <th className="px-6 py-4 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr><td colSpan="6" className="text-center p-8"><Loader2 className="animate-spin mx-auto text-blue-600"/></td></tr>
                                ) : filteredAndSortedExpenses.length === 0 ? (
                                    <tr><td colSpan="6" className="text-center p-8 text-gray-500">
                                        {activeFilter ? "No expenses match this filter." : "No expenses recorded yet."}
                                    </td></tr>
                                ) : (
                                    filteredAndSortedExpenses.map(expense => (
                                        <tr key={expense.id} className="border-b hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap">
                                                {expense.date ? format(expense.date.toDate(), 'MMM d, yyyy') : 'N/A'}
                                            </td>
                                            <td className="px-6 py-4">
                                                <p className="font-semibold text-gray-800">{expense.vendor || 'Unknown Vendor'}</p>
                                                <p className="text-xs text-gray-500 bg-gray-100 inline-block px-2 py-0.5 rounded mt-1">{expense.category}</p>
                                            </td>
                                            <td className="px-6 py-4">
                                                {expense.jobId ? (
                                                    <span className="text-blue-600 font-medium flex items-center gap-1">
                                                        <LinkIcon size={12}/> {getJobDisplayInfo(expense.jobId)}
                                                    </span>
                                                ) : (
                                                    <span className="text-gray-400 italic">-- General Overhead --</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 text-right font-bold text-gray-900">
                                                {formatCurrency(expense.amount || 0)}
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <button 
                                                    onClick={() => handleSyncToQB(expense)}
                                                    disabled={syncingId === expense.id || expense.qbId}
                                                    className={`p-2 rounded-full border transition-colors ${expense.qbId ? 'bg-green-50 border-green-200 text-green-600' : 'hover:bg-gray-100 border-gray-200 text-gray-500'}`}
                                                    title={expense.qbId ? "Synced to QuickBooks" : "Sync to QuickBooks"}
                                                >
                                                    {syncingId === expense.id ? (
                                                        <Loader2 size={16} className="animate-spin" />
                                                    ) : expense.qbId ? (
                                                        <RefreshCw size={16} className="text-green-600" />
                                                    ) : (
                                                        <RefreshCw size={16} />
                                                    )}
                                                </button>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <div className="flex justify-end gap-2">
                                                    {canEditExpense(currentUserProfile, expense) && <button onClick={() => openExpenseModal(expense)} className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"><Edit size={16} /></button>}
                                                    {canDeleteExpense(currentUserProfile) && <button onClick={() => handleDeleteRequest(expense)} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"><Trash2 size={16} /></button>}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* --- MOBILE CARD VIEW --- */}
                    <div className="md:hidden bg-gray-50 p-3 space-y-3">
                        {filteredAndSortedExpenses.map(expense => (
                            <div key={expense.id} className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm relative">
                                {/* Actions (Top Right) */}
                                <div className="absolute top-3 right-3 flex gap-1">
                                    {canEditExpense(currentUserProfile, expense) && <button onClick={() => openExpenseModal(expense)} className="p-1.5 text-gray-400 hover:text-blue-600"><Edit size={16}/></button>}
                                    {canDeleteExpense(currentUserProfile) && <button onClick={() => handleDeleteRequest(expense)} className="p-1.5 text-gray-400 hover:text-red-600"><Trash2 size={16}/></button>}
                                </div>

                                {/* Top Row: Date & Amount */}
                                <div className="flex justify-between items-start mb-1 pr-16">
                                    <div>
                                        <p className="text-xs font-bold text-gray-400 uppercase tracking-wide">{expense.date ? format(expense.date.toDate(), 'MMM d, yyyy') : 'N/A'}</p>
                                        <h3 className="font-bold text-gray-800 text-lg leading-tight">{expense.vendor || 'Unknown Vendor'}</h3>
                                    </div>
                                </div>

                                {/* Middle Row: Category */}
                                <div className="mb-3">
                                    <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded-full">{expense.category}</span>
                                </div>

                                {/* Bottom Row: Job Link & Sync Status */}
                                <div className="flex justify-between items-center pt-3 border-t border-gray-100">
                                    <div className="flex flex-col">
                                        {expense.jobId && (
                                            <span className="text-xs text-blue-600 font-medium flex items-center gap-1 mb-1">
                                                <LinkIcon size={10}/> Job Linked
                                            </span>
                                        )}
                                        <p className="text-xl font-bold text-gray-900">{formatCurrency(expense.amount || 0)}</p>
                                    </div>

                                    <button 
                                        onClick={() => handleSyncToQB(expense)}
                                        disabled={syncingId === expense.id || expense.qbId}
                                        className={`px-3 py-1.5 rounded-full text-xs font-bold border flex items-center gap-1 ${expense.qbId ? 'bg-green-50 border-green-200 text-green-700' : 'bg-white border-gray-300 text-gray-500'}`}
                                    >
                                        {syncingId === expense.id ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                                        {expense.qbId ? 'Synced' : 'Sync QB'}
                                    </button>
                                </div>
                            </div>
                        ))}
                        {filteredAndSortedExpenses.length === 0 && (
                            <p className="text-center text-gray-500 py-6">No expenses found.</p>
                        )}
                    </div>
                </div>
            </div>

            <ExpenseFormModal
                isOpen={isModalOpen}
                onClose={closeExpenseModal}
                onSave={handleSaveExpense}
                expenseToEdit={editingExpense}
                jobs={jobs}
                currentUserProfile={currentUserProfile} 
            />
            
            <ConfirmationModal
                isOpen={!!expenseToDelete}
                onClose={() => setExpenseToDelete(null)}
                onConfirm={handleConfirmDelete}
                title="Delete Expense"
                message={`Are you sure you want to permanently delete this expense? This action cannot be undone.`}
                confirmText={isProcessing ? "Deleting..." : "Delete"}
            />
        </>
    );
};

export default ExpensesView;